import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  Auth,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut
} from '@angular/fire/auth';
import { Database, get, ref, remove, set } from '@angular/fire/database';
import { AuthUser } from '../models/auth-user.model';
import { OrderService } from './order.service';
import { CartService } from './cart.service';
import { CorporateCustomers } from '../models/corporateCustomer.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UnitService } from './unit.service';
import { LanguageService } from '../../shared/services/language.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly user = signal<AuthUser | null>(null);
  readonly isAuthenticated = computed<boolean>(() => this.user() !== null);
  readonly authInitialized = signal<boolean>(false);
  readonly loginDialogVisible = signal<boolean>(false);
  readonly corporateCustomers = signal<CorporateCustomers[]>([]);
  /** Synced from URL `?auth=login` (corporate invite). */
  readonly inviteLoginQueryParam = signal(false);
  /** Hold mandatory date picker until invite login dialog is closed or completed. */
  readonly shouldDeferMandatoryDate = computed(
    () =>
      this.inviteLoginQueryParam() &&
      !this.isAuthenticated() &&
      this.loginDialogVisible()
  );
  private auth = inject(Auth);
  private db = inject(Database);
  private orderService = inject(OrderService);
  private cartService = inject(CartService);
  private http = inject(HttpClient);
  private unitService = inject(UnitService);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private authReady: Promise<void> | null = null;
  /** Bumped on each NavigationEnd so invite-login reacts to URL/query changes. */
  private readonly navigationRevision = signal(0);

  hasInviteLoginQueryParam(): boolean {
    return this.inviteLoginQueryParam();
  }

  async stripInviteLoginQueryParam(): Promise<void> {
    if (!this.inviteLoginQueryParam()) {
      return;
    }
    await this.router.navigate([], {
      queryParams: { auth: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** User dismissed invite login without signing in — allow mandatory date picker. */
  dismissInviteLoginPrompt(): void {
    void this.stripInviteLoginQueryParam();
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.navigationRevision.update((revision) => revision + 1));

    effect(() => {
      this.navigationRevision();
      this.authInitialized();
      this.isAuthenticated();
      this.syncInviteLoginFromUrl();
    });

    // Keep corporate customers in sync with both auth state and current route
    effect(() => {
      const currentUser = this.user();
      const routeName = this.unitService.routeName();
      if (!currentUser || !routeName) {
        this.corporateCustomers.set([]);
        return;
      }
      this.loadCorporateCustomersForUser(currentUser);
    });
  }
  openLoginDialog(): void {
    this.loginDialogVisible.set(true);
  }

  closeLoginDialog(): void {
    this.loginDialogVisible.set(false);
  }

  private syncInviteLoginFromUrl(): void {
    if (!this.authInitialized()) {
      return;
    }
    const hasInvite = this.readInviteLoginQueryParamFromUrl();
    this.inviteLoginQueryParam.set(hasInvite);
    if (!hasInvite) {
      return;
    }
    if (this.isAuthenticated()) {
      void this.stripInviteLoginQueryParam();
      return;
    }
    this.openLoginDialog();
  }

  private readInviteLoginQueryParamFromUrl(): boolean {
    const auth = (this.router.parseUrl(this.router.url).queryParams['auth'] ?? '').toLowerCase();
    return auth === 'login';
  }

  initAuth(): Promise<void> {
    if (this.authReady) return this.authReady;

    this.authReady = new Promise<void>((resolve) => {
      let firstEmission = true;
      onAuthStateChanged(this.auth, async (firebaseUser) => {
        this.orderService.resetOrderStreams();

        try {
          if (!firebaseUser) {
            this.user.set(null);
          } else {
            const profile = await this.loadUserProfile(firebaseUser);
            this.user.set(profile);
          }
        } finally {
          if (firstEmission) {
            firstEmission = false;
            this.authInitialized.set(true);
            resolve();
          }
        }
      });
    });

    return this.authReady;
  }
  private async loadUserProfile(firebaseUser: FirebaseUser): Promise<AuthUser> {
    const uid = firebaseUser.uid;
    const userRef = ref(this.db, `Users/${uid}`);
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      return snapshot.val() as AuthUser;
    }
    return {
      id: uid,
      email: firebaseUser.email ?? '',
      firstName: '',
      lastName: '',
      createdDate: new Date().toISOString(),
    };
  }

  async createAccount(email: string, password: string, firstName: string, lastName: string): Promise<AuthUser> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    const uid = credential.user.uid;
    const profile: AuthUser = {
      id: uid,
      email: credential.user.email ?? email,
      firstName,
      lastName,
      createdDate: new Date().toISOString()
    };

    const userRef = ref(this.db, `Users/${uid}`);
    await set(userRef, profile);

    this.user.set(profile);
    return profile;
  }

  async signIn(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  async updateProfile(update: { phoneNumber?: string }): Promise<AuthUser> {
    const current = this.user();
    if (!current) {
      throw new Error('Not authenticated');
    }

    const next: AuthUser = {
      ...current,
      ...(update.phoneNumber !== undefined && { phoneNumber: update.phoneNumber }),
    };

    const userRef = ref(this.db, `Users/${current.id}`);
    await set(userRef, next);

    this.user.set(next);
    return next;
  }

  async signOut(): Promise<void> {
    this.orderService.resetOrderStreams();
    this.orderService.clearCheckoutDraft(this.unitService.routeName());
    await firebaseSignOut(this.auth);
    this.cartService.clearCart();
    this.user.set(null);
    this.corporateCustomers.set([]);
  }

  private async loadCorporateCustomersForUser(user: AuthUser): Promise<void> {
    const routeName = this.unitService.routeName();
    if (!routeName || !user?.id) {
      this.corporateCustomers.set([]);
      return;
    }
    try {
      const response = await this.http
        .post<{ corporateCustomers: CorporateCustomers[] }>(
          `${environment.pubQueueApiUrlEU}/v1/units/users/corporate/customers`,
          {
            userId: user.id,
            salesLocationRoute: routeName,
          },
        )
        .toPromise();
      this.corporateCustomers.set(
        (response?.corporateCustomers ?? []).map((customer) => ({
          ...customer,
          deliveryLocations: Array.isArray(customer.deliveryLocations)
            ? customer.deliveryLocations.map((loc) => ({
                ...loc,
                subLocations: loc?.subLocations ?? [],
              }))
            : [],
        })),
      );
    } catch {
      this.corporateCustomers.set([]);
    }
  }

  /**
   * Explicit refresh hook for components that know route context is ready
   * (e.g. direct navigation to checkout after app bootstrap).
   */
  async refreshCorporateCustomersForCurrentContext(): Promise<void> {
    const currentUser = this.user();
    if (!currentUser) {
      this.corporateCustomers.set([]);
      return;
    }
    await this.loadCorporateCustomersForUser(currentUser);
  }

  async removePaymentMethod(vendor: string, cardKey: string): Promise<void> {
    const user = this.user();
    if (!user?.id) throw new Error('Not authenticated');
    const cardRef = ref(this.db, `Users/${user.id}/billingInfo/paymentSources/${vendor}/${cardKey}`);
    await remove(cardRef);
    const firebaseUser = this.auth.currentUser;
    if (firebaseUser) {
      const profile = await this.loadUserProfile(firebaseUser);
      this.user.set(profile);
    }
  }

  async resetPassword(email: string): Promise<void> {
    await this.http
      .post(
        `${environment.ApiUrl}/api/v1/auth/resetpw`,
        {
          email,
          languageCode: this.languageService.currentLanguage,
        },
      )
      .toPromise();
  }

}

